/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * Session-cached staff display-name directory for the People roster
 */

import { httpsCallable } from "firebase/functions";
import { firebaseFunctions } from "@lemonadetraining/web-shared";
import type { GetBoxStaffDisplayNamesResponse } from "@lemonadetraining/contracts";

export interface StaffDirectory {
  /** uid → display name for active staff. */
  names: Record<string, string>;
  /** uid → display name for the senders of pending staff invites. */
  senderNames: Record<string, string>;
  ownerUID: string | null;
}

const directoryByBox = new Map<string, Promise<StaffDirectory>>();

const fetchDirectory = async (boxID: string): Promise<StaffDirectory> => {
  const getBoxStaffDisplayNames = httpsCallable<
    { boxID: string; includeInactive?: boolean },
    GetBoxStaffDisplayNamesResponse
  >(firebaseFunctions, "getBoxStaffDisplayNames");
  /* The roster lists suspended and inactive staff, so it needs their names too.
   * The flag is opt-in server-side: the coach pickers share this callable and
   * must keep seeing active staff only. */
  const { data } = await getBoxStaffDisplayNames({ boxID, includeInactive: true });
  /* Validated, not defaulted. `staff: []` is a real answer (a gym with no
   * staff); `staff` MISSING is a broken contract, and swallowing it would
   * render every row as "Name unavailable" — a roster that looks loaded and
   * is not. The caller shows the honest load-failed alert instead. */
  if (!data || !Array.isArray(data.staff)) {
    throw new Error("getBoxStaffDisplayNames returned no staff list");
  }
  const names: Record<string, string> = {};
  data.staff.forEach((row) => {
    /* The callable falls back to the uid when it can resolve no name at all —
     * an erased member, for instance. A raw id sitting in the roster looking
     * like a name nobody recognises is worse than saying we do not have one. */
    if (row?.uid && row.name && row.name !== row.uid) names[row.uid] = row.name;
  });
  return {
    names,
    senderNames: data.pendingInviteSenders ?? {},
    ownerUID: data.ownerUID ?? null,
  };
};

/**
 * Resolve a box's staff display names, server-side, once per session.
 *
 * Names are never denormalised onto the roster documents, so this callable is
 * the only source; caching it per box keeps one round trip per gym per tab
 * rather than one per mount. A rejection is NOT swallowed: a roster of
 * "Name unavailable" rows is not a loaded roster, and the caller shows the
 * honest load-failed alert instead. The failed entry is dropped so a retry
 * genuinely re-fetches rather than replaying the rejection forever.
 *
 * @param boxID - the gym whose staff to resolve
 * @param force - bypass and replace the cached entry (someone just joined)
 */
export const loadStaffDirectory = (
  boxID: string,
  { force = false }: { force?: boolean } = {}
): Promise<StaffDirectory> => {
  if (!force) {
    const cached = directoryByBox.get(boxID);
    if (cached) return cached;
  }
  const pending = fetchDirectory(boxID).catch((error) => {
    directoryByBox.delete(boxID);
    throw error;
  });
  directoryByBox.set(boxID, pending);
  return pending;
};

export const _resetStaffDirectoryForTests = (): void => {
  directoryByBox.clear();
};
